import { useRef, useEffect, useState, useCallback } from 'react';
import { PAPER_SIZES, SCALE_MAP, VIEWPORT_TYPES, createViewport } from '../../constants/paper';
import { GRID_SIZE } from '../../constants/grid';
import { Button } from '../ui';
import ViewportEditor from './ViewportEditor';
import { drawElevation } from '../../utils/drawElevation';
import { drawDimension } from '../canvas/renderers/dimensionRenderer';
import { formatMeasurement } from '../../utils/measurements';
import { computeWallTrims, drawArchitecturalWalls, drawStandardWalls, drawDetailedWalls } from '../canvas/renderers/cornerRenderer';

/**
 * PaperSpaceView - Renders printable sheet layouts with multiple viewports
 */
const PaperSpaceView = ({
  activeFloor,
  activeSheet,
  projectInfo = {},
  isMobile = false,
  wallDetailLevel = 'simple',
  onClose,
  onUpdateSheet,
  onUpdateProjectInfo,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(0.5);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Viewport management
  const [showViewportEditor, setShowViewportEditor] = useState(false);
  const [selectedViewportId, setSelectedViewportId] = useState(null);
  const [draggingViewport, setDraggingViewport] = useState(null);
  const [resizingViewport, setResizingViewport] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Get paper dimensions
  const paperSize = PAPER_SIZES[activeSheet?.size] || PAPER_SIZES.letter;
  const isLandscape = activeSheet?.orientation !== 'portrait';
  const paperWidth = isLandscape ? paperSize.width : paperSize.height;
  const paperHeight = isLandscape ? paperSize.height : paperSize.width;

  // Get viewports from sheet
  const viewports = activeSheet?.viewports || [];

  // Calculate fit-to-screen zoom using window dimensions directly
  const fitToScreen = useCallback(() => {
    const headerHeight = isMobile ? 80 : 50;
    const padding = isMobile ? 40 : 80;

    const containerWidth = window.innerWidth - padding;
    const containerHeight = window.innerHeight - headerHeight - padding;

    const scaleX = containerWidth / paperWidth;
    const scaleY = containerHeight / paperHeight;
    const fitZoom = Math.min(scaleX, scaleY, 1);

    setZoom(fitZoom);
    setPan({ x: 0, y: 0 });
  }, [paperWidth, paperHeight, isMobile]);

  // Fit to screen on mount and when sheet changes
  useEffect(() => {
    fitToScreen();
    window.addEventListener('resize', fitToScreen);
    window.addEventListener('orientationchange', fitToScreen);
    return () => {
      window.removeEventListener('resize', fitToScreen);
      window.removeEventListener('orientationchange', fitToScreen);
    };
  }, [activeSheet?.id, activeSheet?.size, activeSheet?.orientation, fitToScreen]);

  // Viewport CRUD operations
  const handleAddViewport = useCallback((viewport) => {
    if (!onUpdateSheet || !activeSheet) return;
    const newViewports = [...(activeSheet.viewports || []), viewport];
    onUpdateSheet(activeSheet.id, { viewports: newViewports });
    setSelectedViewportId(viewport.id);
  }, [activeSheet, onUpdateSheet]);

  const handleUpdateViewport = useCallback((viewportId, updates) => {
    if (!onUpdateSheet || !activeSheet) return;
    const newViewports = (activeSheet.viewports || []).map(v =>
      v.id === viewportId ? { ...v, ...updates } : v
    );
    onUpdateSheet(activeSheet.id, { viewports: newViewports });
  }, [activeSheet, onUpdateSheet]);

  const handleDeleteViewport = useCallback((viewportId) => {
    if (!onUpdateSheet || !activeSheet) return;
    const newViewports = (activeSheet.viewports || []).filter(v => v.id !== viewportId);
    onUpdateSheet(activeSheet.id, { viewports: newViewports });
    if (selectedViewportId === viewportId) {
      setSelectedViewportId(null);
    }
  }, [activeSheet, onUpdateSheet, selectedViewportId]);

  // State for project info editing
  const [showProjectInfo, setShowProjectInfo] = useState(false);

  // Handle pan (when not dragging viewport)
  const handlePanStart = useCallback((clientX, clientY) => {
    if (draggingViewport || resizingViewport) return;
    setIsPanning(true);
    setPanStart({ x: clientX - pan.x, y: clientY - pan.y });
  }, [pan, draggingViewport, resizingViewport]);

  const handlePanMove = useCallback((clientX, clientY) => {
    if (!isPanning) return;
    setPan({
      x: clientX - panStart.x,
      y: clientY - panStart.y,
    });
  }, [isPanning, panStart]);

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Mouse handlers for the container
  const handleMouseDown = (e) => {
    if (e.target === containerRef.current || e.target.tagName === 'CANVAS') {
      handlePanStart(e.clientX, e.clientY);
    }
  };
  const handleMouseMove = (e) => {
    if (draggingViewport) {
      const deltaX = (e.clientX - dragStart.x) / zoom;
      const deltaY = (e.clientY - dragStart.y) / zoom;
      const viewport = viewports.find(v => v.id === draggingViewport);
      if (viewport) {
        handleUpdateViewport(draggingViewport, {
          x: Math.max(0, viewport.x + deltaX),
          y: Math.max(0, viewport.y + deltaY),
        });
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    } else if (resizingViewport) {
      const deltaX = (e.clientX - dragStart.x) / zoom;
      const deltaY = (e.clientY - dragStart.y) / zoom;
      const viewport = viewports.find(v => v.id === resizingViewport);
      if (viewport) {
        handleUpdateViewport(resizingViewport, {
          width: Math.max(100, viewport.width + deltaX),
          height: Math.max(80, viewport.height + deltaY),
        });
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    } else {
      handlePanMove(e.clientX, e.clientY);
    }
  };
  const handleMouseUp = () => {
    handlePanEnd();
    setDraggingViewport(null);
    setResizingViewport(null);
  };

  // Touch handlers
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const touchStartHandler = (e) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        setIsPanning(true);
        setPanStart({ x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y });
      }
    };

    const touchMoveHandler = (e) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        const touch = e.touches[0];
        setPan({
          x: touch.clientX - panStart.x,
          y: touch.clientY - panStart.y,
        });
      }
    };

    const touchEndHandler = () => {
      setIsPanning(false);
    };

    container.addEventListener('touchstart', touchStartHandler, { passive: false });
    container.addEventListener('touchmove', touchMoveHandler, { passive: false });
    container.addEventListener('touchend', touchEndHandler, { passive: false });

    return () => {
      container.removeEventListener('touchstart', touchStartHandler);
      container.removeEventListener('touchmove', touchMoveHandler);
      container.removeEventListener('touchend', touchEndHandler);
    };
  }, [pan, panStart]);

  // Draw floor plan in a viewport
  const drawFloorPlan = useCallback((ctx, viewport, walls) => {
    if (walls.length === 0) return;

    const drawScale = SCALE_MAP[viewport.scale] || 0.06;

    // Calculate bounds
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    walls.forEach(wall => {
      minX = Math.min(minX, wall.start.x, wall.end.x);
      minY = Math.min(minY, wall.start.y, wall.end.y);
      maxX = Math.max(maxX, wall.start.x, wall.end.x);
      maxY = Math.max(maxY, wall.start.y, wall.end.y);
    });

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    ctx.save();
    ctx.translate(viewport.x + viewport.width / 2, viewport.y + viewport.height / 2);
    ctx.scale(drawScale, drawScale);
    ctx.translate(-centerX, -centerY);

    // Helper to convert inches to model pixels
    const inchesToPixels = (inches) => inches * (GRID_SIZE / 6);

    // Draw walls based on detail level
    if (wallDetailLevel === 'architectural' || wallDetailLevel === 'standard' || wallDetailLevel === 'detailed') {
      const trims = computeWallTrims(walls);
      if (wallDetailLevel === 'architectural') {
        drawArchitecturalWalls(ctx, walls, trims, [], false);
      } else if (wallDetailLevel === 'standard') {
        drawStandardWalls(ctx, walls, trims, [], false);
      } else {
        drawDetailedWalls(ctx, walls, trims, [], false);
      }
    } else {
      walls.forEach(wall => {
        const thicknessInches = wall.type === 'exterior' ? 8 : 4;
        const thickness = inchesToPixels(thicknessInches);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = thickness;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(wall.start.x, wall.start.y);
        ctx.lineTo(wall.end.x, wall.end.y);
        ctx.stroke();
      });
    }

    // Draw doors

    (activeFloor?.doors || []).forEach(door => {
      const wall = walls.find(w => w.id === door.wallId);
      if (!wall) return;

      const dx = wall.end.x - wall.start.x;
      const dy = wall.end.y - wall.start.y;
      const pos = { x: wall.start.x + dx * door.position, y: wall.start.y + dy * door.position };
      const angle = Math.atan2(dy, dx);
      // Convert wall thickness from inches to model pixels
      const thicknessInches = wall.type === 'exterior' ? 8 : 4;
      const thickness = inchesToPixels(thicknessInches);
      // Convert door width from inches to model pixels
      const doorWidthInches = door.width || 36;
      const doorWidth = inchesToPixels(doorWidthInches);

      ctx.save();
      ctx.translate(pos.x, pos.y);
      ctx.rotate(angle);

      ctx.fillStyle = '#fff';
      ctx.fillRect(-doorWidth / 2 - 1, -thickness / 2 - 4, doorWidth + 2, thickness + 8);

      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.arc(-doorWidth / 2, thickness / 2, doorWidth, 0, Math.PI / 2);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.beginPath();
      ctx.moveTo(-doorWidth / 2, thickness / 2);
      ctx.lineTo(-doorWidth / 2, thickness / 2 + doorWidth);
      ctx.stroke();

      ctx.restore();
    });

    // Draw windows
    (activeFloor?.windows || []).forEach(win => {
      const wall = walls.find(w => w.id === win.wallId);
      if (!wall) return;

      const dx = wall.end.x - wall.start.x;
      const dy = wall.end.y - wall.start.y;
      const pos = { x: wall.start.x + dx * win.position, y: wall.start.y + dy * win.position };
      const angle = Math.atan2(dy, dx);
      // Convert wall thickness from inches to model pixels
      const thicknessInches = wall.type === 'exterior' ? 8 : 4;
      const thickness = inchesToPixels(thicknessInches);
      // Convert window width from inches to model pixels
      const winWidthInches = win.width || 48;
      const winWidth = inchesToPixels(winWidthInches);

      ctx.save();
      ctx.translate(pos.x, pos.y);
      ctx.rotate(angle);

      ctx.fillStyle = '#fff';
      ctx.fillRect(-winWidth / 2, -thickness / 2 - 2, winWidth, thickness + 4);

      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-winWidth / 2, -thickness / 2);
      ctx.lineTo(winWidth / 2, -thickness / 2);
      ctx.moveTo(-winWidth / 2, thickness / 2);
      ctx.lineTo(winWidth / 2, thickness / 2);
      ctx.moveTo(-winWidth / 2, 0);
      ctx.lineTo(winWidth / 2, 0);
      ctx.stroke();

      ctx.restore();
    });

    // Draw dimensions
    (activeFloor?.dimensions || []).forEach(dim => {
      drawDimension(ctx, dim, false, drawScale, false, formatMeasurement, 'architectural', 'modern');
    });

    ctx.restore();
  }, [activeFloor]);


  // Render paper space - shared function for display and print
  const renderSheet = useCallback((canvas, forPrint = false) => {
    if (!canvas || !activeSheet || !activeFloor) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;

    // Clear and fill white
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);

    // Draw border
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.strokeRect(24, 24, w - 48, h - 48);

    const walls = activeFloor.walls || [];
    const roofs = activeFloor.roofs || [];
    const doors = activeFloor.doors || [];
    const windows = activeFloor.windows || [];

    // Draw viewports
    viewports.forEach(viewport => {
      // Viewport border - only show when not printing
      if (!forPrint) {
        ctx.strokeStyle = selectedViewportId === viewport.id ? '#00c8ff' : '#ccc';
        ctx.lineWidth = selectedViewportId === viewport.id ? 2 : 1;
        ctx.strokeRect(viewport.x, viewport.y, viewport.width, viewport.height);
      }

      // Clip to viewport
      ctx.save();
      ctx.beginPath();
      ctx.rect(viewport.x, viewport.y, viewport.width, viewport.height);
      ctx.clip();

      // Draw content based on type
      if (viewport.type === 'floor-plan') {
        drawFloorPlan(ctx, viewport, walls);
      } else if (viewport.type.includes('elevation')) {
        const elevation = viewport.type.replace('-elevation', '');
        // Pass options for detailed rendering matching ElevationView
        // Use viewport-specific settings if available, otherwise use defaults matching ElevationView
        const elevationOptions = {
          renderMode: viewport.renderMode || 'color',
          sidingPattern: viewport.sidingPattern || 'horizontal',
          detailedOpenings: true,
          clipPatterns: true,
          showLevelLines: true,
          showGripPoints: false,      // No grip points in Paper Space (not interactive)
          foundationHeightInches: viewport.foundationHeight ?? 12, // Default 12" foundation
          showFoundation: (viewport.foundationHeight ?? 12) > 0,
          marginX: 20,
          marginY: 25,
        };
        drawElevation(ctx, viewport, { walls, roofs, doors, windows }, elevation, elevationOptions);
      }

      ctx.restore();

      // Viewport label
      if (viewport.showLabel) {
        ctx.fillStyle = '#000';
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(viewport.label, viewport.x + viewport.width / 2, viewport.y + viewport.height + 14);
        ctx.font = '8px Arial';
        ctx.fillText(viewport.scale, viewport.x + viewport.width / 2, viewport.y + viewport.height + 24);
      }
    });

    return { walls, roofs, doors, windows, w, h, ctx };
  }, [activeFloor, activeSheet, viewports, selectedViewportId, drawFloorPlan]);

  // Print function - creates temporary canvas without viewport borders
  const handlePrint = useCallback(() => {
    if (!activeSheet || !activeFloor) return;

    // Create a temporary canvas for print rendering
    const printCanvas = document.createElement('canvas');
    printCanvas.width = paperWidth;
    printCanvas.height = paperHeight;

    // Render without viewport borders
    const result = renderSheet(printCanvas, true);
    if (!result) return;

    const { walls, w, h, ctx } = result;

    // Draw the same content as main render (title block, etc.)
    // If no viewports, draw default floor plan
    if (viewports.length === 0) {
      const defaultViewport = {
        x: 50,
        y: 50,
        width: w - 420,
        height: h - 200,
        scale: activeSheet.scale,
      };

      if (walls.length > 0) {
        drawFloorPlan(ctx, defaultViewport, walls);
      }
    }

    // Draw title block
    const margin = 40;
    const titleBlockWidth = 320;
    const tbX = w - margin - titleBlockWidth;
    const tbY = h - margin - 140;
    const tbW = titleBlockWidth;
    const tbH = 140;

    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.strokeRect(tbX, tbY, tbW, tbH);

    ctx.fillStyle = '#000';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(projectInfo.projectName || 'PROJECT NAME', tbX + tbW / 2, tbY + 20);

    ctx.font = '10px Arial';
    ctx.fillText(projectInfo.clientName || '', tbX + tbW / 2, tbY + 35);
    ctx.fillText(projectInfo.address || '', tbX + tbW / 2, tbY + 50);

    ctx.beginPath();
    ctx.moveTo(tbX, tbY + 60);
    ctx.lineTo(tbX + tbW, tbY + 60);
    ctx.stroke();

    ctx.font = '8px Arial';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#666';
    ctx.fillText('DRAWN BY', tbX + 10, tbY + 75);
    ctx.fillText('DATE', tbX + tbW / 2 + 10, tbY + 75);
    ctx.fillText('SCALE', tbX + 10, tbY + 100);
    ctx.fillText('SHEET', tbX + tbW / 2 + 10, tbY + 100);

    ctx.fillStyle = '#000';
    ctx.font = '10px Arial';
    ctx.fillText(projectInfo.drawnBy || '-', tbX + 10, tbY + 88);
    ctx.fillText(projectInfo.date || new Date().toLocaleDateString(), tbX + tbW / 2 + 10, tbY + 88);
    ctx.fillText(activeSheet.scale, tbX + 10, tbY + 113);
    ctx.font = 'bold 16px Arial';
    ctx.fillText(activeSheet.sheetNumber || 'A01', tbX + tbW / 2 + 10, tbY + 115);

    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(tbX, tbY + 120);
    ctx.lineTo(tbX + tbW, tbY + 120);
    ctx.moveTo(tbX + tbW / 2, tbY + 60);
    ctx.lineTo(tbX + tbW / 2, tbY + 120);
    ctx.stroke();

    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(activeSheet.sheetTitle || 'FLOOR PLAN', tbX + tbW / 2, tbY + 133);

    // Get the canvas as a data URL
    const dataUrl = printCanvas.toDataURL('image/png');

    // Open a new window with just the image
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${activeSheet?.sheetNumber || 'Sheet'} - ${activeSheet?.sheetTitle || 'Print'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: #fff;
            }
            img {
              max-width: 100%;
              max-height: 100vh;
              width: auto;
              height: auto;
            }
            @media print {
              body { background: #fff; }
              img {
                max-width: 100%;
                max-height: 100%;
                page-break-inside: avoid;
              }
              @page {
                size: ${isLandscape ? 'landscape' : 'portrait'};
                margin: 0.25in;
              }
            }
          </style>
        </head>
        <body>
          <img src="${dataUrl}" alt="Floor Plan Sheet" onload="window.print(); window.close();" />
        </body>
      </html>
    `);
    printWindow.document.close();
  }, [activeSheet, activeFloor, paperWidth, paperHeight, isLandscape, renderSheet, viewports, projectInfo, drawFloorPlan]);

  // Render paper space
  useEffect(() => {
    const canvas = canvasRef.current;
    const result = renderSheet(canvas, false);
    if (!result) return;

    const { walls, w, h, ctx } = result;

    // If no viewports, draw default floor plan
    if (viewports.length === 0) {
      const defaultViewport = {
        x: 50,
        y: 50,
        width: w - 420,
        height: h - 200,
        scale: activeSheet.scale,
      };

      if (walls.length === 0) {
        ctx.fillStyle = '#999';
        ctx.font = '24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Draw walls in Model Space to see them here', w / 2, h / 2);
        ctx.font = '14px Arial';
        ctx.fillText('Or add viewports using the Viewports button', w / 2, h / 2 + 30);
      } else {
        drawFloorPlan(ctx, defaultViewport, walls);
      }
    }

    // Draw title block
    const margin = 40;
    const titleBlockWidth = 320;
    const tbX = w - margin - titleBlockWidth;
    const tbY = h - margin - 140;
    const tbW = titleBlockWidth;
    const tbH = 140;

    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.strokeRect(tbX, tbY, tbW, tbH);

    ctx.fillStyle = '#000';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(projectInfo.projectName || 'PROJECT NAME', tbX + tbW / 2, tbY + 20);

    ctx.font = '10px Arial';
    ctx.fillText(projectInfo.clientName || '', tbX + tbW / 2, tbY + 35);
    ctx.fillText(projectInfo.address || '', tbX + tbW / 2, tbY + 50);

    ctx.beginPath();
    ctx.moveTo(tbX, tbY + 60);
    ctx.lineTo(tbX + tbW, tbY + 60);
    ctx.stroke();

    ctx.font = '8px Arial';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#666';
    ctx.fillText('DRAWN BY', tbX + 10, tbY + 75);
    ctx.fillText('DATE', tbX + tbW / 2 + 10, tbY + 75);
    ctx.fillText('SCALE', tbX + 10, tbY + 100);
    ctx.fillText('SHEET', tbX + tbW / 2 + 10, tbY + 100);

    ctx.fillStyle = '#000';
    ctx.font = '10px Arial';
    ctx.fillText(projectInfo.drawnBy || '-', tbX + 10, tbY + 88);
    ctx.fillText(projectInfo.date || new Date().toLocaleDateString(), tbX + tbW / 2 + 10, tbY + 88);
    ctx.fillText(activeSheet.scale, tbX + 10, tbY + 113);
    ctx.font = 'bold 16px Arial';
    ctx.fillText(activeSheet.sheetNumber || 'A01', tbX + tbW / 2 + 10, tbY + 115);

    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(tbX, tbY + 120);
    ctx.lineTo(tbX + tbW, tbY + 120);
    ctx.moveTo(tbX + tbW / 2, tbY + 60);
    ctx.lineTo(tbX + tbW / 2, tbY + 120);
    ctx.stroke();

    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(activeSheet.sheetTitle || 'FLOOR PLAN', tbX + tbW / 2, tbY + 133);

  }, [renderSheet, activeSheet, projectInfo, paperWidth, paperHeight, viewports]);

  if (!activeSheet) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#505050',
        zIndex: 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '16px',
      }}>
        <span style={{ color: '#fff', fontSize: '16px' }}>No sheet selected</span>
        <Button variant="primary" onClick={onClose}>Back to Model</Button>
      </div>
    );
  }

  return (
    <div
      className="paper-space-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#505050',
        zIndex: 500,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <div
        className="paper-space-header"
        style={{
          background: 'rgba(8,12,16,0.98)',
          borderBottom: '1px solid rgba(0,200,255,0.2)',
          padding: isMobile ? '8px 10px' : '8px 16px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '8px' : '12px',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button variant="default" onClick={onClose}>
            ← {isMobile ? 'Back' : 'Back to Model Space'}
          </Button>

          {!isMobile && (
            <>
              <span style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>
                {activeSheet.sheetNumber} - {activeSheet.sheetTitle}
              </span>
              <span style={{ color: '#6080a0', fontSize: '10px' }}>
                {PAPER_SIZES[activeSheet.size]?.name || activeSheet.size} | Scale: {activeSheet.scale}
              </span>
            </>
          )}

          <div style={{ flex: 1 }} />

          {/* Project Info button */}
          <Button
            variant={showProjectInfo ? 'primary' : 'default'}
            onClick={() => setShowProjectInfo(!showProjectInfo)}
          >
            Project Info
          </Button>

          {/* Viewports button */}
          <Button
            variant={showViewportEditor ? 'primary' : 'default'}
            onClick={() => setShowViewportEditor(!showViewportEditor)}
          >
            Viewports ({viewports.length})
          </Button>

          {/* Zoom controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => setZoom(z => Math.max(0.1, z - 0.1))}
              style={{
                padding: isMobile ? '8px 12px' : '4px 8px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: isMobile ? '16px' : '12px',
              }}
            >
              -
            </button>
            <span style={{ color: '#fff', fontSize: isMobile ? '12px' : '11px', minWidth: '50px', textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => setZoom(z => Math.min(2, z + 0.1))}
              style={{
                padding: isMobile ? '8px 12px' : '4px 8px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: isMobile ? '16px' : '12px',
              }}
            >
              +
            </button>
            <button
              onClick={fitToScreen}
              style={{
                padding: isMobile ? '8px 10px' : '4px 8px',
                background: 'rgba(0,200,255,0.15)',
                border: '1px solid rgba(0,200,255,0.3)',
                borderRadius: '4px',
                color: '#00c8ff',
                cursor: 'pointer',
                fontSize: isMobile ? '11px' : '10px',
                fontWeight: '500',
              }}
            >
              Fit
            </button>
          </div>

          <button
            onClick={handlePrint}
            style={{
              padding: isMobile ? '8px 12px' : '6px 12px',
              background: 'rgba(0,200,255,0.2)',
              border: '1px solid rgba(0,200,255,0.4)',
              borderRadius: '4px',
              color: '#00c8ff',
              fontSize: isMobile ? '12px' : '11px',
              cursor: 'pointer',
            }}
          >
            Print
          </button>
        </div>

        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontSize: '11px', fontWeight: '500' }}>
              {activeSheet.sheetNumber}
            </span>
            <span style={{ color: '#6080a0', fontSize: '10px' }}>
              {PAPER_SIZES[activeSheet.size]?.name || activeSheet.size}
            </span>
          </div>
        )}
      </div>

      {/* Paper Preview Area */}
      <div
        ref={containerRef}
        className="paper-preview-area"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isPanning ? 'grabbing' : 'grab',
          background: '#505050',
          position: 'relative',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Paper Sheet Container */}
        <div
          id="print-sheet"
          style={{
            width: paperWidth * zoom,
            height: paperHeight * zoom,
            background: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            position: 'relative',
            flexShrink: 0,
            transform: `translate(${pan.x}px, ${pan.y}px)`,
          }}
        >
          <canvas
            ref={canvasRef}
            width={paperWidth}
            height={paperHeight}
            style={{
              width: '100%',
              height: '100%',
            }}
          />

          {/* Viewport drag/resize handles (rendered as HTML overlays) */}
          {viewports.map(viewport => (
            <div
              key={viewport.id}
              style={{
                position: 'absolute',
                left: viewport.x * zoom,
                top: viewport.y * zoom,
                width: viewport.width * zoom,
                height: viewport.height * zoom,
                border: selectedViewportId === viewport.id ? '2px solid #00c8ff' : '1px solid transparent',
                cursor: 'move',
                pointerEvents: 'auto',
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setSelectedViewportId(viewport.id);
                setDraggingViewport(viewport.id);
                setDragStart({ x: e.clientX, y: e.clientY });
              }}
            >
              {/* Resize handle */}
              {selectedViewportId === viewport.id && (
                <div
                  style={{
                    position: 'absolute',
                    right: -4,
                    bottom: -4,
                    width: 12,
                    height: 12,
                    background: '#00c8ff',
                    cursor: 'se-resize',
                    borderRadius: '2px',
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setResizingViewport(viewport.id);
                    setDragStart({ x: e.clientX, y: e.clientY });
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Viewport Editor Panel */}
        {showViewportEditor && (
          <div className="viewport-editor">
            <ViewportEditor
              viewports={viewports}
              selectedViewportId={selectedViewportId}
              onAddViewport={handleAddViewport}
              onUpdateViewport={handleUpdateViewport}
              onDeleteViewport={handleDeleteViewport}
              onSelectViewport={setSelectedViewportId}
              onClose={() => setShowViewportEditor(false)}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* Project Info Editor Panel */}
        {showProjectInfo && (
          <div
            className="project-info-editor"
            style={{
              position: 'absolute',
              top: isMobile ? '60px' : '60px',
              right: isMobile ? '10px' : '16px',
              background: 'rgba(8,12,16,0.98)',
              border: '1px solid rgba(0,200,255,0.2)',
              borderRadius: '10px',
              padding: '14px',
              width: isMobile ? 'calc(100% - 20px)' : '300px',
              maxWidth: '340px',
              maxHeight: isMobile ? '60vh' : '70vh',
              overflowY: 'auto',
              zIndex: 600,
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>
                Project Information
              </span>
              <button
                onClick={() => setShowProjectInfo(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6080a0',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                ×
              </button>
            </div>

            {/* Project Name */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: '#8899aa', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
                Project Name
              </label>
              <input
                type="text"
                value={projectInfo.projectName || ''}
                onChange={(e) => onUpdateProjectInfo?.({ ...projectInfo, projectName: e.target.value })}
                placeholder="Enter project name"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px',
                }}
              />
            </div>

            {/* Client Name */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: '#8899aa', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
                Client Name
              </label>
              <input
                type="text"
                value={projectInfo.clientName || ''}
                onChange={(e) => onUpdateProjectInfo?.({ ...projectInfo, clientName: e.target.value })}
                placeholder="Enter client name"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px',
                }}
              />
            </div>

            {/* Address */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: '#8899aa', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
                Address
              </label>
              <input
                type="text"
                value={projectInfo.address || ''}
                onChange={(e) => onUpdateProjectInfo?.({ ...projectInfo, address: e.target.value })}
                placeholder="Enter address"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px',
                }}
              />
            </div>

            {/* Drawn By */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: '#8899aa', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
                Drawn By
              </label>
              <input
                type="text"
                value={projectInfo.drawnBy || ''}
                onChange={(e) => onUpdateProjectInfo?.({ ...projectInfo, drawnBy: e.target.value })}
                placeholder="Enter your name"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px',
                }}
              />
            </div>

            {/* Date */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: '#8899aa', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
                Date
              </label>
              <input
                type="text"
                value={projectInfo.date || ''}
                onChange={(e) => onUpdateProjectInfo?.({ ...projectInfo, date: e.target.value })}
                placeholder="Enter date"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px',
                }}
              />
            </div>

            {/* Info */}
            <div style={{
              marginTop: '12px',
              padding: '10px',
              background: 'rgba(0,200,255,0.05)',
              borderRadius: '6px',
              fontSize: '10px',
              color: '#6080a0',
            }}>
              This information will appear in the title block of your printed sheets.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaperSpaceView;
